import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Scale, FileText, Download, Upload, CheckCircle2, Video, ShieldAlert, PenLine, ExternalLink, MessageSquare, Loader2, Lock } from "lucide-react";
import { fmtDate } from "./referralConstants";
import { DocActionNudge } from "@/components/DocActionNudge";

const SIGN_TONE = {
  ready_for_signing: "text-[#7C3AED] bg-[#7C3AED]/12", signing_pending: "text-[#7C3AED] bg-[#7C3AED]/12",
  meeting_scheduled: "text-[#2C6480] bg-[#2C6480]/12", waiting_for_professional: "text-[#B7791F] bg-[#B7791F]/12",
  signed_externally: "text-[#2F7D5A] bg-[#2F7D5A]/12", completed: "text-[#2F7D5A] bg-[#2F7D5A]/12",
};

export default function ExternalReviewsSignings() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [completeDoc, setCompleteDoc] = useState(null);
  const [note, setNote] = useState("");
  const fileRef = useRef(null);
  const rowDocId = useRef(null);
  const rowSigned = useRef(false);

  const load = () => api.get("/external/reviews-signings").then(({ data }) => setData(data)).catch(() => setData({ documents: [], meetings: [] }));
  useEffect(() => { load(); }, []);

  const dl = async (d) => { try { await downloadFile(`/external/documents/${d.id}/download`, d.filename || d.title); } catch (e) { toast.error(errMsg(e)); } };
  const upload = (id, signed) => { rowDocId.current = id; rowSigned.current = signed; fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; const id = rowDocId.current;
    if (!file || !id) return;
    setBusyId(id);
    const fd = new FormData(); fd.append("file", file); if (rowSigned.current) fd.append("signed", "1");
    try { await api.post(`/external/documents/${id}/upload`, fd); toast.success("Document uploaded."); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusyId(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  const markComplete = async () => {
    setBusyId(completeDoc.id);
    try { await api.post(`/external/reviews-signings/${completeDoc.id}/complete`, { note: note.trim() || undefined }); toast.success("Marked review complete."); setCompleteDoc(null); setNote(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  if (!data) return <div className="space-y-3"><Skeleton className="h-10" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>;

  if (data.is_professional === false) {
    return (
      <div data-testid="external-reviews-signings-page" className="space-y-5">
        <Card className="p-10 text-center border-[#E6EDF2] max-w-lg mx-auto" data-testid="reviews-not-professional">
          <span className="h-12 w-12 rounded-full bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center mx-auto mb-3"><Lock size={22} /></span>
          <h1 className="font-display text-xl font-bold text-[#252221] mb-1">Professionals only</h1>
          <p className="text-[14px] text-muted-foreground mb-4">This section is only available to professional partners (lawyers, financial advisors, and other licensed reviewers).</p>
          <Button onClick={() => nav("/external")} className="bg-[#1C4358] hover:bg-[#153341] text-white" data-testid="reviews-back-dashboard">Back to dashboard</Button>
        </Card>
      </div>
    );
  }

  const { documents = [], meetings = [], disclaimer } = data;

  return (
    <div data-testid="external-reviews-signings-page" className="space-y-5">
      <div className="flex items-center gap-2">
        <Scale size={22} className="text-[#1C4358]" />
        <h1 className="font-display text-2xl font-bold text-[#252221]">My Reviews & Signings</h1>
      </div>

      <div className="rounded-lg bg-[#FFF7ED] border border-[#FED7AA] px-3 py-2 flex items-start gap-2" data-testid="reviews-disclaimer">
        <ShieldAlert size={15} className="text-[#B7791F] shrink-0 mt-0.5" />
        <span className="text-[12px] text-[#92600E]">{disclaimer}</span>
      </div>

      <DocActionNudge endpoint="/external/reviews-signings/action-summary" testid="reviews-action-nudge"
        onOpen={() => { const el = document.querySelector('[data-testid^="reviews-doc-"]'); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }} />

      <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="reviews-file-input" />

      {/* Documents */}
      <div>
        <h3 className="font-display font-semibold text-[15px] text-[#1C4358] mb-2">Documents assigned to you</h3>
        {documents.length === 0 ? (
          <Card className="p-8 text-center border-[#E6EDF2]" data-testid="reviews-docs-empty"><FileText size={28} className="mx-auto text-[#1C4358]/40 mb-2" /><p className="text-[14px] text-muted-foreground">No review or signing documents assigned to you yet.</p></Card>
        ) : (
          <div className="space-y-2">
            {documents.map((d) => (
              <div key={d.id} className="rounded-xl bg-white border border-[#E6EDF2] p-3" data-testid={`reviews-doc-${d.id}`}>
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center shrink-0"><FileText size={16} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-medium text-[#252221] truncate">{d.title}</div>
                    <div className="text-[11px] text-[#9AA5B1]">{d.doc_type_label} · {fmtDate(d.updated_at)}</div>
                  </div>
                  {d.signing && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${SIGN_TONE[d.signing.signing_status] || "text-[#6B7280] bg-[#6B7280]/12"}`}>{d.signing.signing_status_label}</span>}
                  {d.review_completed && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-[#2F7D5A] bg-[#2F7D5A]/12">Review complete</span>}
                </div>
                {d.signing?.external_signing_link && <a href={d.signing.external_signing_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#7C3AED] text-[12px] font-medium mt-2 hover:underline" data-testid={`reviews-signing-link-${d.id}`}><ExternalLink size={12} /> Open signing link</a>}
                <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                  {d.has_file && <Button size="sm" variant="outline" onClick={() => dl(d)} className="h-8 border-[#D6E0E7] text-[#1C4358] gap-1" data-testid={`reviews-download-${d.id}`}><Download size={13} /> Download</Button>}
                  <Button size="sm" variant="outline" onClick={() => upload(d.id, false)} disabled={busyId === d.id} className="h-8 border-[#D6E0E7] text-[#1C4358] gap-1" data-testid={`reviews-upload-${d.id}`}>{busyId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload reviewed</Button>
                  <Button size="sm" variant="outline" onClick={() => upload(d.id, true)} disabled={busyId === d.id} className="h-8 border-[#7C3AED]/40 text-[#7C3AED] gap-1" data-testid={`reviews-upload-signed-${d.id}`}><PenLine size={13} /> Upload signed</Button>
                  {!d.review_completed && <Button size="sm" onClick={() => setCompleteDoc(d)} className="h-8 bg-[#2F7D5A] hover:bg-[#276a4c] text-white gap-1" data-testid={`reviews-complete-${d.id}`}><CheckCircle2 size={13} /> Mark review complete</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Meetings */}
      <div>
        <h3 className="font-display font-semibold text-[15px] text-[#1C4358] mb-2">Your review / signing rooms</h3>
        {meetings.length === 0 ? (
          <Card className="p-8 text-center border-[#E6EDF2]" data-testid="reviews-meetings-empty"><Video size={28} className="mx-auto text-[#1C4358]/40 mb-2" /><p className="text-[14px] text-muted-foreground">No meeting rooms assigned to you yet.</p></Card>
        ) : (
          <div className="space-y-2">
            {meetings.map((m) => (
              <div key={m.id} className="flex items-center gap-3 rounded-xl bg-white border border-[#E6EDF2] p-3" data-testid={`reviews-meeting-${m.id}`}>
                <span className="h-9 w-9 rounded-lg bg-[#7C3AED]/10 text-[#7C3AED] flex items-center justify-center shrink-0"><Video size={16} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-medium text-[#252221] truncate">{m.title}</div>
                  <div className="text-[11px] text-[#9AA5B1]">{m.meeting_type_label}{m.scheduled_at ? ` · ${fmtDate(m.scheduled_at)}` : ""}</div>
                </div>
                <Button size="sm" onClick={() => nav(`/external/meetings/${m.id}`)} className="h-8 bg-[#1C4358] hover:bg-[#153341] text-white gap-1" data-testid={`reviews-join-${m.id}`}><Video size={13} /> Open room</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!completeDoc} onOpenChange={(o) => { if (!o) { setCompleteDoc(null); setNote(""); } }}>
        <DialogContent data-testid="reviews-complete-dialog">
          <DialogHeader><DialogTitle className="text-[#1C4358]">Mark review complete</DialogTitle>
            <DialogDescription className="text-[13px]">Add an optional note for the MovingMyMama team. This does not constitute legal advice to the client.</DialogDescription>
          </DialogHeader>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Optional note to staff…" data-testid="reviews-complete-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompleteDoc(null); setNote(""); }}>Cancel</Button>
            <Button onClick={markComplete} disabled={busyId === completeDoc?.id} className="bg-[#2F7D5A] hover:bg-[#276a4c] text-white" data-testid="reviews-complete-confirm">Mark complete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
